import React from "react";

/**
 * Executive SectionHeader.
 * Strong, premium hierarchy designed for CEOs, board members, ministers.
 *
 * Props:
 *  - number     → e.g. "— 01" (small overline marker)
 *  - eyebrow    → small caps category label (e.g. "Piliers stratégiques")
 *  - title      → main heading (H2)
 *  - subtitle   → bold supporting line (H3-like) — the strategic promise
 *  - body       → optional explanatory paragraph below subtitle
 *  - dark       → dark canvas (white text)
 *  - align      → "left" | "center"
 *  - titleFont  → "bd" | "eisg"
 *  - size       → "default" | "xl" (xl = massive marketing impact)
 */
const SectionHeader = ({
  number,
  eyebrow,
  title,
  subtitle,
  body,
  dark = false,
  align = "left",
  titleFont = "bd",
  size = "default",
}) => {
  const isCenter = align === "center";
  const containerClass = `${isCenter ? "mx-auto text-center max-w-4xl" : "max-w-4xl"}`;

  // Modérée — confiante mais pas écrasante
  const titleSize =
    size === "xl"
      ? "text-4xl sm:text-5xl lg:text-6xl"
      : "text-3xl sm:text-4xl lg:text-5xl";

  return (
    <div className={containerClass}>
      {(number || eyebrow) && (
        <div className={`flex items-center gap-4 mb-6 ${isCenter ? "justify-center" : ""}`}>
          {number && (
            <span className={`section-num ${dark ? "text-[#D4AF37]" : "text-[#D4AF37]"}`}>{number}</span>
          )}
          {eyebrow && (
            <span
              className={`text-sm sm:text-[15px] tracking-[0.28em] uppercase font-bold ${
                dark ? "text-[#D4AF37]" : "text-[#0A192F]/85"
              }`}
            >
              {eyebrow}
            </span>
          )}
        </div>
      )}
      <h2
        className={`${titleFont === "eisg" ? "font-eisg" : "font-bd"} ${titleSize} font-bold tracking-tight leading-[1.02] ${
          dark ? "text-white" : "text-[#0A192F]"
        }`}
      >
        {title}
      </h2>
      {subtitle && (
        <p
          className={`mt-6 font-bd font-semibold text-lg sm:text-xl lg:text-2xl leading-snug tracking-tight ${
            dark ? "text-white/90" : "text-[#0A192F]/90"
          } max-w-3xl ${isCenter ? "mx-auto" : ""}`}
        >
          {subtitle}
        </p>
      )}
      {body && (
        <p
          className={`mt-5 text-base sm:text-lg leading-relaxed ${
            dark ? "text-white/70" : "text-[#0A192F]/65"
          } max-w-2xl font-body ${isCenter ? "mx-auto" : ""}`}
        >
          {body}
        </p>
      )}
    </div>
  );
};

export default SectionHeader;
